/**
 * catalystDisplay — Markets row catalyst display (UI bugfix 2026-06-13).
 *
 * Restores visible catalyst headlines on Markets rows. Pure, deterministic, and
 * entity-SAFE: the backend (markets_classification_fix) already downgrades a
 * CATALYST_VERIFIED status to a review state whenever entity/direction checks
 * fail, so a row that still reads CATALYST_VERIFIED here is safe to display.
 * This module re-does NO entity matching and NEVER fabricates a headline.
 *
 * Fallback order (Part A):
 *   1. verified, entity-safe headline (status CATALYST_VERIFIED + non-junk headline)
 *   2. review / rejected / conflict state label (catalyst exists but not usable)
 *   3. signal_reason
 *   4. reason (backend)
 *   5. fallbackReason (caller-supplied, e.g. the board's synthesized reason)
 *   6. "No verified catalyst"
 * Junk headlines ("(untitled result)", empty, null/none) are never rendered.
 */
export type CatalystKind = "verified" | "review" | "none";

export interface CatalystDisplay {
  text: string;
  tooltip: string;
  kind: CatalystKind;
  status: string | null;
}

const VERIFIED_STATUSES = new Set(["CATALYST_VERIFIED"]);

// Catalyst exists but is NOT a usable headline -> show the state, never the headline.
const REVIEW_LABELS: Record<string, string> = {
  NEEDS_CATALYST_REVIEW: "Catalyst needs review",
  CATALYST_REVIEW_REQUIRED: "Catalyst review required",
  INVESTIGATING_CATALYST: "Investigating catalyst…",
  HIGH_RISK_REVIEW: "High risk — review required",
  AVOID: "Avoid — do not act",
  CATALYST_DIRECTION_CONFLICT: "Catalyst direction conflict",
  UNDERLYING_DIRECTION_CONFLICT: "Underlying direction conflict",
  ENTITY_MATCH_REJECTED: "Catalyst rejected — wrong entity",
};

const JUNK_HEADLINES = new Set([
  "", "(UNTITLED RESULT)", "UNTITLED RESULT", "NULL", "NONE", "N/A", "NA", "UNDEFINED", "-", "—",
]);

function clean(v: unknown): string {
  return v === null || v === undefined ? "" : String(v).trim();
}
function isJunkHeadline(h: string): boolean {
  return JUNK_HEADLINES.has(h.toUpperCase());
}

export function deriveCatalystDisplay(
  row: Record<string, any> | null | undefined,
  opts?: { fallbackReason?: string },
): CatalystDisplay {
  const r = row || {};
  const status = clean(r.catalyst_status).toUpperCase() || null;
  const headline = clean(r.primary_catalyst_display) || clean(r.primary_catalyst);
  const snippet = clean(r.primary_catalyst_snippet);
  const source = clean(r.primary_catalyst_source);
  const ts = clean(r.primary_catalyst_ts);
  const entity = clean(r.entity_match_status);
  const signalReason = clean(r.signal_reason);
  const reason = clean(r.reason);
  const fallbackReason = clean(opts?.fallbackReason);
  const headlineOk = headline.length > 0 && !isJunkHeadline(headline);

  // Full-detail tooltip (native title; no panel/modal).
  const tip: string[] = [];
  if (headlineOk) tip.push(headline);
  if (snippet && snippet !== headline) tip.push(snippet);
  if (source) tip.push("Source: " + source);
  if (ts) tip.push("As of: " + ts);
  if (status) tip.push("Status: " + status);
  if (entity) tip.push("Entity match: " + entity);
  const detailTip = tip.join("\n");

  // 1. verified + entity-safe headline
  if (status && VERIFIED_STATUSES.has(status) && headlineOk) {
    return { text: headline, tooltip: detailTip || headline, kind: "verified", status };
  }
  // 2. review / rejected / conflict state
  if (status && REVIEW_LABELS[status]) {
    const label = REVIEW_LABELS[status];
    return { text: label, tooltip: detailTip || label, kind: "review", status };
  }
  // 3/4/5. signal_reason -> backend reason -> caller fallback reason
  const fb = signalReason || reason || fallbackReason;
  if (fb) {
    return { text: fb, tooltip: detailTip ? detailTip + "\n" + fb : fb, kind: "none", status };
  }
  // 6. compact fallback
  return { text: "No verified catalyst", tooltip: status ? "Status: " + status : "No verified catalyst", kind: "none", status };
}
